"use client";

import { useEffect } from "react";
import { useRouter, useParams } from "next/navigation";
import { useForm, Controller } from "react-hook-form";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useApiPost } from "@admin/_lib/api-hooks";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { RichHtmlEditor } from "@admin/_components/rich-html-editor";
import { MultiSelectCheckboxField } from "@admin/_components/multi-select-checkbox-field";

interface CategoryFormValues {
  category_name: string;
  priority: string;
  role: string[];
  description: string;
  image: string;
  status: boolean;
}

const BASE_URL = process.env.NEXT_PUBLIC_API_CLOUDFRONT || "https://api.divineinfinitebeing.com/";
const CF_BASE = "https://d37pbainmkhx6o.cloudfront.net/";

function resolveImageUrl(img: unknown): string {
  if (!img) return "";
  if (typeof img === "string") return img;
  if (typeof img === "object" && img !== null) {
    const o = img as Record<string, string>;
    if (o.fileservername) return `${CF_BASE}${o.baseurl ?? ""}${o.fileservername}`;
  }
  return "";
}

export function CategoryForm() {
  const router = useRouter();
  const params = useParams();
  const id = params?.id as string | undefined;
  const isEdit = !!id;
  const { data: session } = useSession();

  const { control, handleSubmit, reset, register, formState: { errors } } = useForm<CategoryFormValues>({
    defaultValues: { category_name: "", priority: "", role: [], description: "", image: "", status: true },
  });

  const { data: record, isLoading: isFetching } = useQuery({
    queryKey: ["admin/training-edit", id],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}admin/training-edit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify({ _id: id }),
      });
      const data = await res.json();
      return data.results?.res ?? data.results ?? data;
    },
    enabled: isEdit && !!session?.token,
  });

  useEffect(() => {
    if (!record) return;
    const r = record as Record<string, unknown>;
    const roleVal = Array.isArray(r.role)
      ? (r.role as unknown[]).map(String)
      : r.role
        ? [String(r.role)]
        : [];
    reset({
      category_name: String(r.category_name ?? ""),
      priority: String(r.priority ?? ""),
      role: roleVal,
      description: String(r.description ?? ""),
      image: resolveImageUrl(r.image),
      status: r.status === 1 || r.status === true,
    });
  }, [record, reset]);

  const addMutation = useApiPost("admin/training-category-add", { invalidateKeys: ["admin/training-list", "admin/training-list-count"] });
  const editMutation = useApiPost("admin/training-category-update", { invalidateKeys: ["admin/training-list", "admin/training-list-count"] });
  const mutation = isEdit ? editMutation : addMutation;

  async function onSubmit(values: CategoryFormValues) {
    try {
      const body: Record<string, unknown> = {
        category_name: values.category_name,
        priority: values.priority ? parseFloat(values.priority) : 0,
        role: values.role,
        description: values.description,
        status: values.status ? 1 : 0,
      };
      if (values.image) body.image = values.image;
      if (isEdit) body._id = id;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Category updated" : "Category created");
      router.push("/admin/training/categories");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  if (isEdit && isFetching) {
    return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96 rounded-xl" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => router.back()}><ArrowLeft className="size-4" /></Button>
        <div className="h-6 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <h1 className="text-xl font-semibold">{isEdit ? "Edit Training Category" : "Add Training Category"}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
          <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
              Category Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Category Name <span className="text-destructive">*</span></Label>
                <Input {...register("category_name", { required: "Category name is required" })} placeholder="Enter category name" />
                {errors.category_name && <p className="text-xs text-destructive">{errors.category_name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Priority <span className="text-destructive">*</span></Label>
                <Input type="number" {...register("priority", { required: "Priority is required" })} placeholder="1" />
                {errors.priority && <p className="text-xs text-destructive">{errors.priority.message}</p>}
              </div>
            </div>

            {/* Role multi-select */}
            <MultiSelectCheckboxField
              control={control}
              name="role"
              label="Role"
              endpoint="admin/fetch-role-list"
              valueKey="slug"
              labelKey="role_name"
            />

            {/* Description rich editor */}
            <div className="space-y-1.5">
              <Label>Description <span className="text-destructive">*</span></Label>
              <Controller
                name="description"
                control={control}
                rules={{ required: "Description is required" }}
                render={({ field }) => <RichHtmlEditor value={field.value} onChange={field.onChange} />}
              />
              {errors.description && <p className="text-xs text-destructive">{errors.description.message}</p>}
            </div>

            {/* Thumbnail image URL */}
            <div className="space-y-1.5">
              <Label>Thumbnail Image URL</Label>
              <Input {...register("image")} placeholder="https://… (CloudFront URL)" />
            </div>

            {/* Status switch */}
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div
                    role="switch" aria-checked={field.value} tabIndex={0}
                    onClick={() => field.onChange(!field.value)}
                    onKeyDown={(e) => e.key === " " && field.onChange(!field.value)}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${field.value ? "bg-orange-500" : "bg-muted"}`}
                  >
                    <span className={`pointer-events-none inline-block size-4 rounded-full bg-white shadow transition-transform ${field.value ? "translate-x-4" : "translate-x-0"}`} />
                  </div>
                  <span className="text-sm">Active</span>
                </label>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <button type="submit" disabled={mutation.isPending}
            className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 btn-orange">
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {mutation.isPending ? "Saving…" : isEdit ? "Update Category" : "Create Category"}
          </button>
          <Button type="button" variant="outline" onClick={() => router.back()}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
